import React from 'react';
import { Select, Text, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import appRoutes from '@/constants/routes/app';
import { useCreateLegalDocument } from '@/hooks/legal/legal';
import { TLegalDocument } from '@/types/Legal.types';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import CustomButton from '@/components/atoms/CustomButton';
import queryKeys from '@/constants/queryKeys';
import { LEGAL_DOCUMENT_TYPE_LABELS, LEGAL_DOCUMENT_TYPES } from '@/constants/stringVars';

type Props = {
  isOpen: boolean;
  onClose?: () => void;
};

const CreateLegalDocumentModal = ({ isOpen, onClose }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isPending, mutate } = useCreateLegalDocument((document: TLegalDocument) => {
    navigate({ to: `${appRoutes.eula}/${document._id}` });
    queryClient.invalidateQueries({ queryKey: [queryKeys.legalDocument.allLegalDocuments] });
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        type: yup.string().required('Type is required.'),
      }),
    ),
  });

  const onSubmit = async ({ type }: { type: string }) => {
    mutate({ type });
  };
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = methods;

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} title="Create New Legal Document">
      <VStack>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <VStack spacing={'20px'} width={'100%'}>
              <VStack width={'100%'} alignItems={'flex-start'} spacing={0}>
                <Select placeholder="Select New Legal Document Type" {...register('type')}>
                  {LEGAL_DOCUMENT_TYPES.map((documentType) => (
                    <option key={documentType} value={documentType}>
                      {LEGAL_DOCUMENT_TYPE_LABELS[documentType]}
                    </option>
                  ))}
                </Select>
                <Text color={'red.500'} fontSize={'14px'}>
                  {errors.type?.message ?? ''}
                </Text>
              </VStack>
              <CustomButton
                label="Create"
                buttonType="submit"
                disabled={!isValid}
                isLoading={isPending}
                width={'100%'}
              />
            </VStack>
          </form>
        </FormProvider>
      </VStack>
    </ModalContainer>
  );
};

export default CreateLegalDocumentModal;
